import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttentionItem } from '@mailpilot/shared';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { deviceName } from '../lib/desktop';
import { useToast } from '../components/ui/Toast';

type ListRes = { items: AttentionItem[] };

export function useAttentionItems(status: 'open' | 'all' = 'open') {
  return useQuery({ queryKey: qk.attention(status), queryFn: () => api('GET /api/attention', { query: { status } }), refetchInterval: 60_000 });
}

function patchItem(qc: ReturnType<typeof useQueryClient>, id: string, patch: Partial<AttentionItem>) {
  for (const s of ['open', 'all'] as const) {
    qc.setQueryData<ListRes>(qk.attention(s), (prev) =>
      prev
        ? {
            items:
              s === 'open' && patch.status === 'acknowledged'
                ? prev.items.filter((i) => i.id !== id)
                : prev.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
          }
        : prev,
    );
  }
}

/** Explicit "Seen / Acknowledged" — the ONLY action that acknowledges an attention item. */
export function useAcknowledge() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (id: string) => api('POST /api/attention/:id/acknowledge', { params: { id }, body: { deviceName: await deviceName() } }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.attentionRoot });
      const prev = { open: qc.getQueryData<ListRes>(qk.attention('open')), all: qc.getQueryData<ListRes>(qk.attention('all')) };
      patchItem(qc, id, { status: 'acknowledged', acknowledgedAt: new Date().toISOString() });
      return prev;
    },
    onError: (err, _id, prev) => {
      if (prev?.open) qc.setQueryData(qk.attention('open'), prev.open);
      if (prev?.all) qc.setQueryData(qk.attention('all'), prev.all);
      toast.error(err);
    },
    onSuccess: () => toast.success('Acknowledged — ringing stops on all devices.'),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.attentionRoot });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
    },
  });
}

export function useAcknowledgeAll() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => api('POST /api/attention/acknowledge-all', { body: { deviceName: await deviceName() } }),
    onSuccess: (r) => toast.success(`Acknowledged ${r.count} item${r.count === 1 ? '' : 's'}.`),
    onError: (e) => toast.error(e),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.attentionRoot }),
  });
}

export function useSnooze() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, minutes }: { id: string; minutes?: number }) => api('POST /api/attention/:id/snooze', { params: { id }, body: { minutes } }),
    onSuccess: (r) => {
      patchItem(qc, r.item.id, r.item);
      toast.info(`Snoozed until ${r.item.snoozedUntil ? new Date(r.item.snoozedUntil).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : 'later'}. Not acknowledged — it will ring again.`);
    },
    onError: (e) => toast.error(e),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.attentionRoot }),
  });
}
